import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { loadArtworkTexture } from '../artwork-texture.js';
import { genererCanauxRuban, MATCAP_GAIN, CHROME_BOX, SATIN_BOX } from './lib/matcap.js';
import { vdjColor, VdjComfort } from './lib/vdjLook.js';

// Contrat du rig partagé : interdit. Le cube a sa propre lumière (matcap).
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'twist',
  // le brief dit 'objects' ; la famille de l'orchestrateur est 'object'
  family: 'object',
  danceability: 0.72,
  energyLevel: 0.64,
  bpmMin: 85,
  bpmMax: 135,
  movement: 0.60,
  tonality: 0.70,
  enabled: true
};

// TWIST : un casse-tête cubique 3 x 3 x 3 qui flotte à côté de la pochette
// DOM. Ses six faces portent la pochette découpée en 9 tuiles. Mélangé au
// début du morceau, il joue la séquence inverse, un mouvement de couche par
// temps, et se résout sur le premier drop. Pop, électro-pop, house, hip-hop.
// Règles de kiosk-app/CLAUDE.md R1 à R17 ; rythme « coupes sur la phrase »
// (R9 : danceability 0,72).
//
// L'ÉTAT LOGIQUE (JS, testable sans GPU) : pour chacun des 27 cubelets, sa
// position entière (-1, 0, 1)³ et son orientation (matrice entière 3 x 3).
// Un mouvement = un quart de tour d'UNE couche de face (R, L, U, D, F, B et
// leurs inverses : axe, couche ±1, sens). L'état n'est remis à jour qu'à la
// FIN d'un mouvement (positions et quaternions en attributs d'instance, 27 x
// 7 flottants, une fois par temps) ; la rotation EN COURS est en uniforms
// (axe, couche, angle) et le vertex shader tourne les cubelets de la couche
// active. Aucune interpénétration par construction : les cubelets mesurent
// 0,94 pour un pas de 1, la couche qui tourne est séparée des autres par un
// plan perpendiculaire à l'axe (jeu de 0,06), et elle tourne en bloc rigide.
//
// LES TUILES : chaque cubelet garde l'identité de sa case d'origine (iHome).
// Sur une face extérieure de la case d'origine, le fragment shader dessine un
// carré aux coins arrondis, légèrement en retrait (une rainure sombre), qui
// affiche la pochette à l'endroit où cette case la place sur cette face du
// cube résolu : les six faces portent la même image, droite vue de dehors.
//
// LE SCÉNARIO : au début du morceau, mélange instantané (non animé) par une
// suite aléatoire de N mouvements ; N = le nombre de temps jusqu'au premier
// drop de l'analyse, sinon jusqu'à 60 % du morceau, sinon 20 - un mouvement
// par temps tombe donc juste sur la cible. Si l'analyse arrive après coup, la
// file est rallongée d'un détour (k mouvements puis leurs inverses) ou jouée
// plus vite (jusqu'à 4 mouvements par temps). Résolu : 4 mesures de pause
// (la pochette sur toutes les faces), puis un « shuffle » de 20 mouvements en
// 2 mesures, puis la résolution recommence.
//
// LE RYTHME
//   TEMPS   un mouvement de couche : quart de tour en ease-in-out sur 70 %
//           du temps, pause sur le reste
//   MESURE  le cube entier tourne d'un quart de tour (lacet), en douceur
//   PHRASE  le cube change de côté de la pochette : il rétrécit jusqu'à rien
//           puis grandit de l'autre côté (il ne passe JAMAIS derrière elle)
//   DROP    la pochette est complète (résolution forcée si besoin), tuiles
//           +30 %, flash léger, puis 4 mesures de rotation lente qui montre
//           les faces
//
// LA PLACE (R6) : le cube tient dans une sphère de rayon r (sa diagonale) ;
// le cercle projeté de cette sphère est posé entre la pochette (gonflée) et
// le bord (marge 5 %), hors des zones d'interface ; il mesure 45 % de la
// hauteur d'écran quand la place le permet. Caméra comme Tesla : le plan
// z = 0 se lit en unités de hauteur d'écran.
//
// LE RENDU : un InstancedMesh de 27 cubes arrondis (normales exactes), une
// matcap noire laquée (chrome de lib/matcap.js assombri, deux reflets nets),
// tuiles satinées éclairées d'en haut ; une flaque de lumière très faible sous
// le cube porte son ombre de contact. MSAA 4x dans une cible privée (R5).

const TAU = Math.PI * 2;
const CAM_D = 3.6;
const PITCH = 0.50;                 // rad : le dessus tourné vers la caméra (trois quarts)
const CUBELET = 0.94, ROUND = 0.085;
const STICKER_HALF = 0.355, STICKER_R = 0.075;
const SIZE_FRAC = 0.45;             // la hauteur d'écran du cube, vu de trois quarts
// la silhouette du cube de trois quarts (lacet 45°, tangage PITCH) : hauteur =
// arête x (cos PITCH + sqrt 2 x sin PITCH) ; sa sphère : arête x sqrt 3 / 2
const SILHOUETTE = Math.cos(0.50) + Math.SQRT2 * Math.sin(0.50);
const FRAME_MARGIN = 0.10, COVER_MARGIN = 0.06;
const PERSPECTIVE_PAD = 1.10;       // le cercle projeté d'une sphère proche de la caméra
const MOVE_FRAC = 0.70;             // la part du temps où la couche tourne
const HOLD_BEATS = 16, SHUFFLE_MOVES = 20, SHUFFLE_BEATS = 8, SHOW_BEATS = 16;
const DEFAULT_MOVES = 20;
const MAX_SCRAMBLE = 600, MAX_PER_BEAT = 4;
const NO_BPM = 112;
const DROP_SCORE_MIN = 0.7, DROP_STALE_MS = 1500;
const DROP_GAIN = 0.30;
const TILE_LUM_MAX = 0.72;          // une tuile reste sous le seuil de bloom
const FALLBACK_A = 0x5a3cff, FALLBACK_B = 0xff3c8c;

const TIER = { 0: { round: 2, msaa: true }, 1: { round: 3, msaa: true }, 2: { round: 4, msaa: true } };

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  try { return new URLSearchParams(location.search).get(name); } catch (e) { return null; }
}
export function rng(seed) { let s = (seed >>> 0) || 1; return () => { s = (s * 1664525 + 1013904223) >>> 0; return s / 4294967296; }; }
function hashString(str) { let h = 2166136261; for (let i = 0; i < (str || '').length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 16777619); } return h >>> 0; }
// le nombre de mouvements qui finissent avant une cible à `beats` temps de
// l'horloge `clock` : départs sur les temps entiers suivants, chacun tourne
// sur 70 % de son temps
export function movesForTarget(clock, beats) {
  const first = Math.floor(clock) + 1, target = clock + beats;
  return Math.max(0, Math.floor(target - MOVE_FRAC - first) + 1);
}
export const easeInOut = (u) => { const x = clamp01(u); return x * x * (3 - 2 * x); };

// ------------------------------------------------------------ le casse-tête
// Les mouvements de face : axe (0 x, 1 y, 2 z), couche (+1 / -1), sens (signe
// de l'angle autour de +axe). R tourne dans le sens horaire vu de +x : -90°.
export const MOVES = {
  R: { axis: 0, layer: 1, dir: -1 }, L: { axis: 0, layer: -1, dir: 1 },
  U: { axis: 1, layer: 1, dir: -1 }, D: { axis: 1, layer: -1, dir: 1 },
  F: { axis: 2, layer: 1, dir: -1 }, B: { axis: 2, layer: -1, dir: 1 }
};
export const MOVE_NAMES = ['R', 'L', 'U', 'D', 'F', 'B'];
export function move(name, prime) { const m = MOVES[name]; return { name: name + (prime ? "'" : ''), axis: m.axis, layer: m.layer, dir: prime ? -m.dir : m.dir }; }
export function inverse(m) { return { name: m.name.endsWith("'") ? m.name.slice(0, -1) : m.name + "'", axis: m.axis, layer: m.layer, dir: -m.dir }; }
export function inverseSeq(seq) { return seq.slice().reverse().map(inverse); }

// la rotation entière d'un quart de tour : matrice 3 x 3 (lignes)
export function quarter(axis, dir) {
  const c = 0, s = dir;
  if (axis === 0) return [[1, 0, 0], [0, c, -s], [0, s, c]];
  if (axis === 1) return [[c, 0, s], [0, 1, 0], [-s, 0, c]];
  return [[c, -s, 0], [s, c, 0], [0, 0, 1]];
}
const mulV = (M, v) => [M[0][0] * v[0] + M[0][1] * v[1] + M[0][2] * v[2], M[1][0] * v[0] + M[1][1] * v[1] + M[1][2] * v[2], M[2][0] * v[0] + M[2][1] * v[1] + M[2][2] * v[2]];
const mulM = (A, B) => A.map((row) => [0, 1, 2].map((j) => row[0] * B[0][j] + row[1] * B[1][j] + row[2] * B[2][j]));
const I3 = () => [[1, 0, 0], [0, 1, 0], [0, 0, 1]];

export function newState() {
  const cubes = [];
  for (let x = -1; x <= 1; x++) for (let y = -1; y <= 1; y++) for (let z = -1; z <= 1; z++) cubes.push({ home: [x, y, z], pos: [x, y, z], rot: I3() });
  return { cubes };
}
// la couche d'un mouvement : les cubelets dont la coordonnée sur l'axe vaut la couche
export function layerOf(state, m) { return state.cubes.filter((c) => c.pos[m.axis] === m.layer); }
export function applyMove(state, m) {
  const R = quarter(m.axis, m.dir);
  for (const c of layerOf(state, m)) { c.pos = mulV(R, c.pos); c.rot = mulM(R, c.rot); }
  return state;
}
export function isSolved(state) {
  for (const c of state.cubes) {
    if (c.pos[0] !== c.home[0] || c.pos[1] !== c.home[1] || c.pos[2] !== c.home[2]) return false;
    for (let i = 0; i < 3; i++) for (let j = 0; j < 3; j++) if (c.rot[i][j] !== (i === j ? 1 : 0)) return false;
  }
  return true;
}
// une suite aléatoire : jamais deux fois la même face de suite (ni son
// inverse), ni trois fois le même axe
export function randomMoves(r, n, last) {
  const out = [];
  let prev = last || null, prev2 = null;
  while (out.length < n) {
    const name = MOVE_NAMES[Math.floor(r() * 6)];
    const m = move(name, r() < 0.5);
    if (prev && prev.axis === m.axis && prev.layer === m.layer) continue;
    if (prev && prev2 && prev.axis === m.axis && prev2.axis === m.axis) continue;
    out.push(m); prev2 = prev; prev = m;
  }
  return out;
}

// --------------------------------------------- les faces : la pochette droite
// Pour la face de normale (axe, signe), les axes « droite » et « haut » de
// l'image vue de dehors (le dessus a son haut vers le fond, le dessous vers
// l'avant). uv d'un point P (repère d'origine du cube, pas 1) : (P.droite + 1,5) / 3.
export const FACE_AXES = {
  '0+': { right: [0, 0, -1], up: [0, 1, 0] }, '0-': { right: [0, 0, 1], up: [0, 1, 0] },
  '1+': { right: [1, 0, 0], up: [0, 0, -1] }, '1-': { right: [1, 0, 0], up: [0, 0, 1] },
  '2+': { right: [1, 0, 0], up: [0, 1, 0] }, '2-': { right: [-1, 0, 0], up: [0, 1, 0] }
};
export function faceUV(axis, sign, P) {
  const f = FACE_AXES[axis + (sign > 0 ? '+' : '-')];
  const d = (a) => a[0] * P[0] + a[1] * P[1] + a[2] * P[2];
  return [(d(f.right) + 1.5) / 3, (d(f.up) + 1.5) / 3];
}

// ------------------------------------------------------------- la place (R6)
// Le cercle (rayon pr, à l'écran) du cube : entre la pochette gonflée et le
// bord, hors des zones haut droite / haut gauche (y > 0,76) et bas droite (y
// < -0,8). Renvoie le centre x (côté +), y, le rayon projeté et le rayon de la
// sphère dans le monde.
export function twistLayout(A, sc, sh) {
  const inner = Math.max(sc.x + sh.x, sh.x - sc.x) + COVER_MARGIN;
  const outer = A - FRAME_MARGIN;
  // le rayon voulu : celui de la sphère d'un cube dont la silhouette fait
  // 45 % de l'écran (hauteur 2), agrandi de la perspective
  const want = (2 * SIZE_FRAC / SILHOUETTE) * Math.sqrt(3) / 2 * PERSPECTIVE_PAD;
  // la place : la largeur entre pochette et bord, la hauteur sous les zones
  // du haut (y < 0,76) et au-dessus de celle du bas (y > -0,8)
  const pr = Math.max(0.12, Math.min(want, (outer - inner) / 2, 0.76 - 0.04, 0.8 - 0.04));
  const x = Math.max(inner + pr, Math.min(outer - pr, (inner + outer) / 2));
  return { x, y: 0, pr, r: pr / PERSPECTIVE_PAD };
}

// ------------------------------------------------------------- la géométrie
// la boîte arrondie à normales exactes (même construction que Tesla)
export function roundedBox(w, r, k) {
  const half = w / 2;
  const samples = () => { const v = []; for (let i = 0; i <= k; i++) v.push(-half + r * i / k); for (let i = k; i >= 0; i--) v.push(half - r * i / k); return v; };
  const S = samples();
  const pos = [], nrm = [], idx = [];
  for (let a = 0; a < 3; a++) for (const s of [-1, 1]) {
    const u = (a + 1) % 3, v = (a + 2) % 3, base = pos.length / 3;
    for (let j = 0; j < S.length; j++) for (let i = 0; i < S.length; i++) {
      const P = [0, 0, 0]; P[a] = s * half; P[u] = S[i]; P[v] = S[j];
      const q = P.map((c) => Math.max(-half + r, Math.min(half - r, c)));
      let n = [P[0] - q[0], P[1] - q[1], P[2] - q[2]];
      const l = Math.hypot(n[0], n[1], n[2]) || 1; n = n.map((c) => c / l);
      pos.push(q[0] + n[0] * r, q[1] + n[1] * r, q[2] + n[2] * r); nrm.push(n[0], n[1], n[2]);
    }
    const nu = S.length;
    for (let j = 0; j < nu - 1; j++) for (let i = 0; i < nu - 1; i++) {
      const p0 = base + j * nu + i, p1 = p0 + 1, p2 = p0 + nu, p3 = p2 + 1;
      if (s > 0) idx.push(p0, p1, p3, p0, p3, p2); else idx.push(p0, p3, p1, p0, p2, p3);
    }
  }
  return { pos: new Float32Array(pos), nrm: new Float32Array(nrm), idx };
}

const VERTEX = `
  precision highp float;
  attribute vec3 iHome;
  attribute vec3 iPos;
  attribute vec4 iQuat;
  uniform float uAxis;
  uniform float uLayer;
  uniform float uAngle;
  uniform mat3 uCubeRot;
  uniform vec3 uCenter;
  uniform float uScale;
  varying vec3 vLocal;
  varying vec3 vNLocal;
  varying vec3 vHome;
  varying vec3 vN;
  varying vec3 vW;
  vec3 qrot(vec4 q, vec3 v) { return v + 2.0 * cross(q.xyz, cross(q.xyz, v) + q.w * v); }
  vec3 turn(vec3 p, float c, float s) {
    if (uAxis < 0.5) return vec3(p.x, c * p.y - s * p.z, s * p.y + c * p.z);
    if (uAxis < 1.5) return vec3(c * p.x + s * p.z, p.y, -s * p.x + c * p.z);
    return vec3(c * p.x - s * p.y, s * p.x + c * p.y, p.z);
  }
  void main() {
    vec3 pc = iPos + qrot(iQuat, position);
    vec3 nc = qrot(iQuat, normal);
    // la couche active tourne (l'état logique est celui d'AVANT le mouvement)
    float coord = uAxis < 0.5 ? iPos.x : (uAxis < 1.5 ? iPos.y : iPos.z);
    if (abs(coord - uLayer) < 0.5) {
      float c = cos(uAngle), s = sin(uAngle);
      pc = turn(pc, c, s); nc = turn(nc, c, s);
    }
    vec3 w = uCenter + uScale * (uCubeRot * pc);
    vLocal = position; vNLocal = normal; vHome = iHome;
    vN = normalize(uCubeRot * nc);
    vW = w;
    gl_Position = projectionMatrix * viewMatrix * vec4(w, 1.0);
  }
`;

const FRAGMENT = `
  precision highp float;
  uniform sampler2D uMat;
  uniform sampler2D uCover;
  uniform float uTileGain;
  uniform float uFlash;
  uniform float uDebugUV;
  varying vec3 vLocal;
  varying vec3 vNLocal;
  varying vec3 vHome;
  varying vec3 vN;
  varying vec3 vW;
  const float HALF = ${(CUBELET / 2).toFixed(3)};
  float sdRound(vec2 p, float h, float r) { vec2 q = abs(p) - vec2(h - r); return length(max(q, 0.0)) + min(max(q.x, q.y), 0.0) - r; }
  // les axes droite / haut de l'image sur chaque face (voir FACE_AXES)
  vec2 faceUV(float axis, float sgn, vec3 P) {
    vec2 rp;
    if (axis < 0.5) rp = vec2(-sgn * P.z, P.y);
    else if (axis < 1.5) rp = vec2(P.x, -sgn * P.z);
    else rp = vec2(sgn * P.x, P.y);
    return (rp + 1.5) / 3.0;
  }
  void main() {
    vec3 n = normalize(vN);
    vec3 nv = normalize(mat3(viewMatrix) * n);
    vec4 m = texture2D(uMat, nv.xy * 0.49 + 0.5);
    // la laque noire : le chrome du studio assombri, deux reflets nets
    float body = m.b * m.b * ${MATCAP_GAIN.toFixed(2)};
    vec3 lacquer = vec3(0.012, 0.012, 0.016) + vec3(0.9, 0.92, 1.0) * (body * 0.10 + m.a * ${(CHROME_BOX * 0.72).toFixed(3)});
    // la face plate du cubelet, et si c'est une face EXTÉRIEURE de sa case d'origine
    vec3 an = abs(vNLocal);
    float axis = an.x > an.y && an.x > an.z ? 0.0 : (an.y > an.z ? 1.0 : 2.0);
    float comp = axis < 0.5 ? vNLocal.x : (axis < 1.5 ? vNLocal.y : vNLocal.z);
    float sgn = sign(comp);
    float homeC = axis < 0.5 ? vHome.x : (axis < 1.5 ? vHome.y : vHome.z);
    vec3 col = lacquer;
    if (abs(comp) > 0.999 && abs(homeC - sgn) < 0.5) {
      vec2 fp = axis < 0.5 ? vLocal.yz : (axis < 1.5 ? vLocal.zx : vLocal.xy);
      float d = sdRound(fp, ${STICKER_HALF.toFixed(3)}, ${STICKER_R.toFixed(3)});
      float fw = max(fwidth(d), 1e-4);
      float tile = 1.0 - smoothstep(-fw, fw, d);
      // la rainure : le pourtour de la tuile, en retrait, plus sombre que la laque
      float groove = 1.0 - 0.75 * (1.0 - smoothstep(0.0, 0.025, d)) * smoothstep(-fw, fw, d);
      vec2 uv = faceUV(axis, sgn, vHome + vLocal);
      vec3 cover = texture2D(uCover, uv).rgb;
      // ?twistuv=1 : l'uv brut (u, v, 1) sur la tuile, pour la sonde
      if (uDebugUV > 0.5) { gl_FragColor = vec4(mix(vec3(0.0), vec3(uv, 1.0), tile), 1.0); return; }
      float l = dot(cover, vec3(0.2126, 0.7152, 0.0722));
      cover *= min(1.0, ${TILE_LUM_MAX.toFixed(2)} / max(l, 1e-4));
      // satiné : le spot du dessus, un reflet large et doux (canal satin)
      float top = clamp(n.y * 0.5 + 0.5, 0.0, 1.0);
      // le bord de la tuile, en biseau : un liseré clair du côté du haut
      float bevel = smoothstep(-0.03, -0.005, d) * (1.0 - smoothstep(-0.005, 0.0, d));
      vec3 satin = cover * (0.62 + 0.38 * top) * uTileGain + vec3(0.06) * m.g * ${SATIN_BOX.toFixed(2)} + cover * 0.15 * bevel;
      col = mix(lacquer * groove, satin, tile);
    }
    col += col * uFlash;
    gl_FragColor = vec4(col, 1.0);
  }
`;

// la flaque de lumière sous le cube : l'ombre de contact (R12 : nulle au bord du quad)
const FLOOR_VERTEX = `
  varying vec2 vP;
  void main() { vP = position.xy; gl_Position = projectionMatrix * viewMatrix * modelMatrix * vec4(position, 1.0); }
`;
const FLOOR_FRAGMENT = `
  precision mediump float;
  uniform vec3 uTint;
  uniform float uShadowR;
  varying vec2 vP;
  void main() {
    float d = length(vP);                         // le quad : rayon 1
    float pool = 0.028 * exp(-d * d * 3.5) * (1.0 - smoothstep(0.55, 0.92, d));
    float shadow = 1.0 - 0.80 * exp(-d * d / (uShadowR * uShadowR));
    gl_FragColor = vec4(uTint * pool * shadow, 1.0);
  }
`;
const DISPLAY_VERTEX = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
const DISPLAY_FRAGMENT = `
  precision mediump float;
  uniform sampler2D uTex;
  varying vec2 vUv;
  void main() { gl_FragColor = vec4(texture2D(uTex, vUv).rgb, 1.0); }
`;

// la pochette de repli : un dégradé de la palette et un disque, pour que les
// tuiles mélangées se distinguent
function fallbackCover(palette) {
  const src = palette && (palette.vividColors || palette.dominantColors) || [];
  const a = vdjColor(src[0] != null ? src[0] : FALLBACK_A, new THREE.Color()), b = vdjColor(src[1] != null ? src[1] : FALLBACK_B, new THREE.Color());
  const N = 64, data = new Uint8Array(N * N * 4), c = new THREE.Color();
  for (let j = 0; j < N; j++) for (let i = 0; i < N; i++) {
    const x = i / (N - 1), y = j / (N - 1);
    c.copy(a).lerp(b, clamp01(0.5 * (x + y)));
    if (Math.hypot(x - 0.38, y - 0.58) < 0.22) c.lerp(new THREE.Color(1, 1, 1), 0.65);
    c.convertLinearToSRGB();
    const o = (j * N + i) * 4; data[o] = c.r * 255; data[o + 1] = c.g * 255; data[o + 2] = c.b * 255; data[o + 3] = 255;
  }
  const t = new THREE.DataTexture(data, N, N, THREE.RGBAFormat);
  t.colorSpace = THREE.SRGBColorSpace; t.minFilter = t.magFilter = THREE.LinearFilter; t.needsUpdate = true;
  return t;
}

let MATCAP = null;
function matcapTexture() {
  if (MATCAP) return MATCAP;
  const m = genererCanauxRuban(256);
  const t = new THREE.DataTexture(m.data, m.size, m.size, THREE.RGBAFormat, THREE.UnsignedByteType);
  t.colorSpace = THREE.NoColorSpace; t.minFilter = t.magFilter = THREE.LinearFilter; t.generateMipmaps = false; t.needsUpdate = true;
  MATCAP = t;
  return t;
}

// =============================================================================

export class TwistVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null && TIER[o.qualityTier] ? o.qualityTier : 2;
    this._L = TIER[this._tier];
    const n = parseInt(urlParam('twistmoves'), 10);
    this._forceMoves = Number.isFinite(n) && n >= 0 ? n : null;
    this._forceSolved = urlParam('twistsolved') === '1';
    this._debugUV = urlParam('twistuv') === '1';
    this._post = { bloomStrength: 0.22, bloomThreshold: 0.82, grain: 0, vignette: 0.30, aberration: 0.0008, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 };
    this._comfort = new VdjComfort();
    if (urlParam('twistprobe') === '1' && typeof window !== 'undefined') window.__twist = this;
  }

  get postProcessing() { return this._post; }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    this._prevBackground = scene.background;
    scene.background = new THREE.Color(0x000000);

    // le cube : 27 cubelets instanciés
    const box = roundedBox(CUBELET, ROUND, this._L.round);
    const g = new THREE.InstancedBufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(box.pos, 3));
    g.setAttribute('normal', new THREE.BufferAttribute(box.nrm, 3));
    g.setIndex(box.idx);
    this._iHome = new Float32Array(27 * 3); this._iPos = new Float32Array(27 * 3); this._iQuat = new Float32Array(27 * 4);
    g.setAttribute('iHome', new THREE.InstancedBufferAttribute(this._iHome, 3));
    g.setAttribute('iPos', new THREE.InstancedBufferAttribute(this._iPos, 3));
    g.setAttribute('iQuat', new THREE.InstancedBufferAttribute(this._iQuat, 4));
    g.instanceCount = 27;
    g.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e3);
    this._geo = g;
    this._fallback = fallbackCover(null);
    this._U = {
      uAxis: { value: 0 }, uLayer: { value: 9 }, uAngle: { value: 0 },
      uCubeRot: { value: new THREE.Matrix3() }, uCenter: { value: new THREE.Vector3() }, uScale: { value: 0.3 },
      uMat: { value: matcapTexture() }, uCover: { value: this._fallback },
      uTileGain: { value: 1 }, uFlash: { value: 0 }, uDebugUV: { value: this._debugUV ? 1 : 0 }
    };
    this._mat = new THREE.ShaderMaterial({ uniforms: this._U, vertexShader: VERTEX, fragmentShader: FRAGMENT });
    this._mesh = new THREE.Mesh(g, this._mat);
    this._mesh.frustumCulled = false;

    this._FU = { uTint: { value: new THREE.Vector3(1, 1, 1) }, uShadowR: { value: 0.35 } };
    this._floorGeo = new THREE.PlaneGeometry(2, 2);
    this._floor = new THREE.Mesh(this._floorGeo, new THREE.ShaderMaterial({ uniforms: this._FU, vertexShader: FLOOR_VERTEX, fragmentShader: FLOOR_FRAGMENT, depthWrite: false, transparent: true, blending: THREE.AdditiveBlending }));
    this._floor.frustumCulled = false; this._floor.rotation.x = -Math.PI / 2; this._floor.renderOrder = -1;

    // MSAA 4x : le cube dans une scène privée, rendu dans une cible
    // multi-échantillonnée affichée par un quad (la chaîne partagée n'a pas d'AA)
    this._private = new THREE.Scene();
    this._private.add(this._floor, this._mesh);
    this._msaaTarget = null;
    this._dispU = { uTex: { value: null } };
    this._display = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._dispU, vertexShader: DISPLAY_VERTEX, fragmentShader: DISPLAY_FRAGMENT, depthTest: false, depthWrite: false }));
    this._display.frustumCulled = false; this._display.renderOrder = -1000;
    const self = this;
    this._display.onBeforeRender = function (renderer, sc, cam) { self._renderPrivate(renderer, cam); };
    scene.add(this._display);

    // l'état
    this._state = newState();
    this._time = 0;
    this._clock = 0; this._clockPrev = 0; this._targetOffset = null;
    this._beatCount = 0; this._barCount = 0; this._phaseBeat = null; this._phaseBar = null; this._phasePhrase = null;
    this._bpm = NO_BPM;
    this._queue = []; this._cur = null; this._perBeat = 1; this._nextStart = 1; this._planBpm = null;
    this._phase = 'solve'; this._phaseUntil = 0; this._targetBeat = null;
    this._trackKey = undefined;
    this._marksRef = undefined; this._drops = []; this._dropIdx = 0; this._lastNowMs = null; this._lastMode = null;
    this._side = 1; this._sideFrom = 1; this._sideSwitch = null;
    this._yawBars = 0; this._barTurn = null;
    this._show = null; this._flash = 0;
    this._artUrl = undefined; this._coverTex = null;
    this._r = rng(1);
    this._log = [];
    this._layoutKey = '';
    this._placeCamera();
    this._uploadState();
  }

  // ---------------------------------------------------------------- l'état
  _uploadState() {
    const q = new THREE.Quaternion(), m4 = new THREE.Matrix4();
    this._state.cubes.forEach((c, i) => {
      this._iHome.set(c.home, i * 3); this._iPos.set(c.pos, i * 3);
      const R = c.rot;
      m4.set(R[0][0], R[0][1], R[0][2], 0, R[1][0], R[1][1], R[1][2], 0, R[2][0], R[2][1], R[2][2], 0, 0, 0, 0, 1);
      q.setFromRotationMatrix(m4);
      this._iQuat[i * 4] = q.x; this._iQuat[i * 4 + 1] = q.y; this._iQuat[i * 4 + 2] = q.z; this._iQuat[i * 4 + 3] = q.w;
    });
    for (const n of ['iHome', 'iPos', 'iQuat']) this._geo.getAttribute(n).needsUpdate = true;
  }

  // le nombre de temps jusqu'à la cible : le premier drop à venir, sinon 60 %
  // du morceau ; null si on ne sait pas
  _beatsToTarget(profile) {
    const now = profile.estimatedPosition != null ? profile.estimatedPosition : null;
    const bps = this._bpm / 60;
    if (now != null) {
      for (const at of this._drops) if (at / 1000 > now + 0.5) return { beats: (at / 1000 - now) * bps, drop: true };
      if (!this._drops.length && profile.duration > 0 && 0.6 * profile.duration > now + 2) return { beats: (0.6 * profile.duration - now) * bps, drop: false };
    }
    return null;
  }

  // un nouveau morceau : mélange instantané, la file = la séquence inverse
  _newTrack(profile) {
    this._r = rng(hashString(String(profile.trackId || '')) ^ 0x9e3779b9);
    this._state = newState();
    this._cur = null; this._show = null; this._U.uLayer.value = 9; this._U.uAngle.value = 0;
    const tgt = this._forceMoves != null || this._forceSolved ? null : this._beatsToTarget(profile);
    // les mouvements partent sur les temps entiers à venir ; le dernier finit
    // de tourner à 70 % de son temps : n = (temps entiers jusqu'à la cible) - 1
    let n = tgt ? movesForTarget(this._clock, tgt.beats) : DEFAULT_MOVES;
    n = Math.max(8, Math.min(MAX_SCRAMBLE, n));
    const scramble = randomMoves(this._r, n);
    for (const m of scramble) applyMove(this._state, m);
    this._queue = inverseSeq(scramble);
    this._targetBeat = tgt ? this._clock + tgt.beats : null;
    this._targetIsDrop = !!(tgt && tgt.drop);
    this._planBpm = this._bpm;
    this._phase = 'solve'; this._perBeat = 1; this._nextStart = Math.floor(this._clock) + 1;
    // débogage : l'état après n mouvements de résolution, ou résolu ; figé
    if (this._forceSolved) { for (const m of this._queue) applyMove(this._state, m); this._queue = []; this._phase = 'frozen'; }
    else if (this._forceMoves != null) { const k = Math.min(this._forceMoves, this._queue.length); for (const m of this._queue.splice(0, k)) applyMove(this._state, m); this._phase = 'frozen'; }
    this._uploadState();
    this._log.push({ what: 'track', moves: n, target: tgt ? +tgt.beats.toFixed(1) : null, drop: this._targetIsDrop });
  }

  // l'analyse est arrivée après le mélange : la file s'ajuste à la cible
  _replan(profile) {
    this._planBpm = this._bpm;
    if (this._phase !== 'solve' || this._forceMoves != null || this._forceSolved) return;
    const tgt = this._beatsToTarget(profile);
    if (!tgt) return;
    const M = this._queue.length;
    // les mouvements qui tiennent encore : départs à partir du prochain créneau
    const next = Math.max(this._nextStart, Math.floor(this._clock) + 1);
    const R = Math.max(0, Math.floor(this._clock + tgt.beats - MOVE_FRAC - next) + 1);
    this._targetBeat = this._clock + tgt.beats; this._targetIsDrop = tgt.drop;
    if (R > M + 1) {
      const k = Math.min(Math.floor((R - M) / 2), MAX_SCRAMBLE);
      const det = randomMoves(this._r, k, this._cur ? this._cur.m : null);
      this._queue = det.concat(inverseSeq(det), this._queue);
    }
    this._log.push({ what: 'replan', beats: R, moves: this._queue.length });
  }

  _tickDrops(profile) {
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks ? tl.marks : null;
    let changed = false;
    if (marks !== this._marksRef) {
      this._marksRef = marks; this._drops = []; changed = true;
      if (marks) for (const m of marks) if (m.type === 'drop' && m.score >= DROP_SCORE_MIN) this._drops.push(m.atMs);
      this._drops.sort((a, b) => a - b);
      this._dropIdx = 0;
    }
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (now == null) return { fired: false, changed };
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) {
      this._dropIdx = 0;
      while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) this._dropIdx++;
    }
    this._lastNowMs = now;
    let fired = false;
    while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) {
      if (now - this._drops[this._dropIdx] < DROP_STALE_MS) fired = true;
      this._dropIdx++;
    }
    return { fired, changed };
  }

  _cover(profile) {
    const url = profile.artworkUrl || null;
    if (url === this._artUrl) return;
    this._artUrl = url;
    const self = this;
    if (this._fallback) this._fallback.dispose();
    this._fallback = fallbackCover(profile.artworkPalette);
    if (!this._coverTex) this._U.uCover.value = this._fallback;
    // la flaque sous le cube : la couleur de la pochette, saturée (R4)
    const pal = profile.artworkPalette, src = pal && (pal.vividColors || pal.dominantColors) || [];
    const tint = vdjColor(src[0] != null ? src[0] : FALLBACK_A, new THREE.Color());
    this._FU.uTint.value.set(tint.r, tint.g, tint.b);
    if (!url) { if (this._coverTex) { this._coverTex.dispose(); this._coverTex = null; } this._U.uCover.value = this._fallback; return; }
    loadArtworkTexture(url, { wrap: THREE.ClampToEdgeWrapping, mipmaps: true }, (tex) => {
      if (!self._scene || url !== self._artUrl) { tex.dispose(); return; }
      if (self._coverTex) self._coverTex.dispose();
      self._coverTex = tex; self._U.uCover.value = tex;
    }, () => { if (self._scene && url === self._artUrl) self._U.uCover.value = self._fallback; });
  }

  _placeCamera() {
    const cam = this._camera;
    cam.fov = THREE.MathUtils.radToDeg(2 * Math.atan(1 / CAM_D));
    cam.near = 0.05; cam.far = 40;
    cam.position.set(0, 0, CAM_D); cam.rotation.set(0, 0, 0); cam.up.set(0, 1, 0);
    cam.updateProjectionMatrix(); cam.updateMatrixWorld();
  }

  update(deltaTime, profile) {
    const dt = Math.min(Math.max(deltaTime, 0), 0.1);
    this._placeCamera();
    this._cover(profile);
    // le tempo AVANT le plan d'un nouveau morceau (sinon il se calcule au
    // tempo de repli, puis rattrape)
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 0;
    const bp = profile.beatPhase, barIn = profile.barPhase;
    const hasGrid = bp != null && bpm > 0;
    this._bpm = hasGrid ? THREE.MathUtils.clamp(bpm, 60, 200) : NO_BPM;
    const drops = this._tickDrops(profile);
    const key = profile.trackId || null;
    if (key !== this._trackKey) { this._trackKey = key; this._newTrack(profile); }
    else if (drops.changed) this._replan(profile);
    if (profile.playbackState !== 'PLAYING') { this._pose(); return; }   // pause : tout gèle
    this._time += dt;

    // l'horloge, en temps : intégrée au tempo, ramenée sur la grille (Domino)
    this._clockPrev = this._clock;
    if (hasGrid) {
      if (this._phaseBeat != null && bp < this._phaseBeat - 0.5) this._beatCount++;
      this._phaseBeat = bp;
      let target;
      if (barIn != null) {
        if (this._phaseBar != null && barIn < this._phaseBar - 0.5) this._barCount++;
        this._phaseBar = barIn;
        target = 4 * (this._barCount + barIn);
      } else target = this._beatCount + bp;
      if (this._targetOffset == null) this._targetOffset = 4 * Math.round((this._clock - target) / 4);
      const err = target + this._targetOffset - this._clock;
      if (Math.abs(err) > 2) { this._targetOffset = 4 * Math.round((this._clock - target) / 4); this._clock = target + this._targetOffset; }
      else { const rate = this._bpm / 60; this._clock += Math.max(dt * rate * 0.5, dt * rate + err * Math.min(1, dt / 0.5)); }
    } else { this._clock += dt * this._bpm / 60; this._targetOffset = null; }
    if (hasGrid && this._planBpm && Math.abs(this._bpm - this._planBpm) / this._planBpm > 0.02) this._replan(profile);
    const clock = this._clock;
    const bar = Math.floor(clock / 4) !== Math.floor(this._clockPrev / 4);
    let phrase = false;
    const ph = profile.phrasePhase;
    if (ph != null) { if (this._phasePhrase != null && ph < this._phasePhrase - 0.5) phrase = true; this._phasePhrase = ph; }
    else phrase = Math.floor(clock / 16) !== Math.floor(this._clockPrev / 16);

    // DROP : la pochette complète, 4 mesures de présentation
    const director = profile.director;
    let drop = hasGrid && drops.fired;
    if (director && director.mode === 'drop' && this._lastMode !== 'drop' && director.hasReliableGrid) drop = true;
    this._lastMode = director ? director.mode : null;
    if (drop && this._phase !== 'frozen' && (!this._show || clock - this._show.start > 4)) this._onDrop(clock);

    this._tickMoves(clock);
    if (bar) this._barTurn = { start: clock };
    if (phrase && this._phase !== 'frozen' && !this._show && !this._sideSwitch) { this._sideSwitch = { start: clock, to: -this._side }; }
    if (this._sideSwitch && clock - this._sideSwitch.start >= 1) { this._side = this._sideSwitch.to; this._sideSwitch = null; }
    if (this._barTurn && clock - this._barTurn.start >= 1) { this._yawBars++; this._barTurn = null; }
    if (this._show && clock - this._show.start >= SHOW_BEATS) { this._show = null; this._startShuffle(clock); }
    this._flash *= Math.exp(-dt / 0.15);
    this._pose();
  }

  _onDrop(clock) {
    // pas encore résolu (analyse tardive) : la résolution est forcée - une coupe
    if (this._cur) { applyMove(this._state, this._cur.m); this._cur = null; }
    if (!isSolved(this._state)) { for (const m of this._queue) applyMove(this._state, m); this._log.push({ what: 'forced', left: this._queue.length }); }
    this._queue = []; this._uploadState();
    this._U.uLayer.value = 9;
    this._phase = 'show'; this._show = { start: clock };
    if (this._comfort.requestFlash(this._time)) this._flash = 1;
    this._log.push({ what: 'drop', beat: +clock.toFixed(2), solved: isSolved(this._state) });
  }

  _startShuffle(clock) {
    const mv = randomMoves(this._r, SHUFFLE_MOVES);
    this._queue = mv.slice(); this._shuffled = mv;
    this._phase = 'shuffle'; this._perBeat = SHUFFLE_MOVES / SHUFFLE_BEATS;
    this._nextStart = Math.ceil(clock);
  }

  // LES MOUVEMENTS : un par créneau (1 temps ; plus court en rattrapage ou en
  // shuffle) ; quart de tour en ease-in-out sur 70 % du créneau
  _tickMoves(clock) {
    if (this._phase === 'frozen' || this._phase === 'show') return;
    if (this._cur) {
      const u = (clock - this._cur.start) * this._cur.per;
      if (u >= 1) {
        applyMove(this._state, this._cur.m); this._uploadState();
        this._cur = null; this._U.uLayer.value = 9; this._U.uAngle.value = 0;
      } else {
        this._U.uAxis.value = this._cur.m.axis; this._U.uLayer.value = this._cur.m.layer;
        this._U.uAngle.value = this._cur.m.dir * (Math.PI / 2) * easeInOut(u / MOVE_FRAC);
      }
    }
    if (!this._cur) {
      if (!this._queue.length) {
        if (this._phase === 'shuffle') {
          // mélangé : la résolution repart, un mouvement par temps
          this._queue = inverseSeq(this._shuffled); this._phase = 'solve'; this._perBeat = 1; this._nextStart = Math.ceil(clock);
          this._targetBeat = null;
        } else if (this._phase === 'solve') {
          this._phase = 'hold'; this._phaseUntil = clock + HOLD_BEATS;
          this._log.push({ what: 'solved', beat: +clock.toFixed(2), target: this._targetBeat });
        } else if (this._phase === 'hold' && clock >= this._phaseUntil) {
          // un drop connu tout proche : on l'attend résolu
          if (!(this._targetIsDrop && this._targetBeat != null && this._targetBeat > clock && this._targetBeat - clock < 32)) this._startShuffle(clock);
        }
        return;
      }
      // le créneau : 1 temps en résolution (plus court en rattrapage si la
      // file dépasse les temps qui restent), 0,4 temps en shuffle
      let p = this._phase === 'shuffle' ? SHUFFLE_MOVES / SHUFFLE_BEATS : 1;
      if (this._phase === 'solve' && this._targetBeat != null) {
        const R = this._targetBeat - this._nextStart - MOVE_FRAC, M = this._queue.length;
        if (M - 1 > R) p = Math.min(MAX_PER_BEAT, Math.ceil(M / Math.max(R + 1, 0.25)));
      }
      this._perBeat = p;
      if (clock < this._nextStart) return;
      const start = clock - this._nextStart > 1 / p ? Math.floor(clock * p) / p : this._nextStart;
      this._cur = { m: this._queue.shift(), start, per: p };
      this._nextStart = start + 1 / p;
      this._U.uAxis.value = this._cur.m.axis; this._U.uLayer.value = this._cur.m.layer; this._U.uAngle.value = 0;
    }
  }

  // la pose du cube : place (R6), côté, lacet par mesure, présentation au drop
  _pose() {
    // R19 : placé d'après le centre de composition, pas d'après la pochette
    const sc = safeZoneUniforms.uStageCenter.value, sh = safeZoneUniforms.uStageHalfSize.value;
    const A = safeZoneUniforms.uAspect.value || this._camera.aspect || 16 / 9;
    const L = twistLayout(A, sc, sh);
    const clock = this._clock;
    let side = this._side, scale = 1;
    if (this._sideSwitch) {
      const u = clamp01(clock - this._sideSwitch.start);
      if (u < 0.5) scale = 1 - easeInOut(u * 2); else { side = this._sideSwitch.to; scale = easeInOut((u - 0.5) * 2); }
    }
    // le cube de rayon de sphère L.r : arête 3 (pas) -> diagonale 1,5 x sqrt(3)
    const s = L.r / (1.5 * Math.sqrt(3)) * scale;
    this._U.uScale.value = Math.max(s, 1e-4);
    this._U.uCenter.value.set(side * L.x, L.y, 0);
    // le lacet : un quart de tour par mesure (en douceur sur un temps), dérive lente
    let yaw = Math.PI / 4 + (Math.PI / 2) * (this._yawBars + (this._barTurn ? easeInOut(clock - this._barTurn.start) : 0)) + 0.18 * Math.sin(TAU * clock / 64);
    let pitch = PITCH;
    let gain = 1;
    if (this._show) {
      const u = clamp01((clock - this._show.start) / SHOW_BEATS);
      yaw += TAU * easeInOut(u);
      // le tangage bascule du dessus (0,5) au dessous (-0,6) puis revient : les
      // faces restent à l'endroit (un demi-tour les montrait tête en bas)
      pitch -= 1.1 * 0.5 * (1 - Math.cos(TAU * u));
      gain = 1 + DROP_GAIN * (1 - 0.6 * u);
    }
    const m = new THREE.Matrix4().makeRotationX(pitch).multiply(new THREE.Matrix4().makeRotationY(yaw));
    this._U.uCubeRot.value.setFromMatrix4(m);
    this._U.uTileGain.value = gain;
    this._U.uFlash.value = 0.35 * this._flash;
    // la flaque et l'ombre : sous le cube
    this._floor.position.set(side * L.x, L.y - L.pr - 0.10, 0);
    this._floor.scale.setScalar(Math.max(L.pr * 1.2 * scale, 1e-4));
    this._FU.uShadowR.value = 0.38 * scale + 1e-3;
    this._layout = L;
  }

  _renderPrivate(renderer, camera) {
    const cur = renderer.getRenderTarget();
    let w, h;
    if (cur) { w = cur.width; h = cur.height; } else { const b = renderer.getDrawingBufferSize(_size); w = b.x; h = b.y; }
    if (!this._msaaTarget || this._msaaTarget.width !== w || this._msaaTarget.height !== h) {
      if (this._msaaTarget) this._msaaTarget.dispose();
      this._msaaTarget = new THREE.WebGLRenderTarget(w, h, { samples: this._L.msaa ? 4 : 0, depthBuffer: true, minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter });
    }
    const cc = renderer.getClearColor(_cc), ca = renderer.getClearAlpha();
    renderer.setRenderTarget(this._msaaTarget);
    renderer.setClearColor(0x000000, 1);
    renderer.clear(true, true, false);
    renderer.render(this._private, camera);
    renderer.setRenderTarget(cur);
    renderer.setClearColor(cc, ca);
    this._dispU.uTex.value = this._msaaTarget.texture;
  }

  getProbe() {
    return {
      tier: this._tier, phase: this._phase, queue: this._queue.length, cur: this._cur ? this._cur.m.name : null, solved: isSolved(this._state),
      clock: +this._clock.toFixed(3), side: this._side, target: this._targetBeat, layout: this._layout, log: this._log.slice(-8)
    };
  }

  dispose() {
    if (this._scene) {
      if (this._display) { this._display.onBeforeRender = null; this._scene.remove(this._display); }
      this._scene.background = this._prevBackground || null;
    }
    for (const m of [this._display, this._mesh, this._floor]) if (m) { m.geometry.dispose(); m.material.dispose(); }
    if (this._msaaTarget) this._msaaTarget.dispose();
    if (this._coverTex) this._coverTex.dispose();
    if (this._fallback) this._fallback.dispose();
    if (typeof window !== 'undefined' && window.__twist === this) window.__twist = null;
    this._scene = null;
  }
}

const _size = new THREE.Vector2();
const _cc = new THREE.Color();
